import { Injectable } from '@angular/core';
import { catchAndContinue, filterUndefined } from 'src/app/lego-utils';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { ISpecificationTableUI } from 'src/app/models';
import { ITablePagination, TaskListHeader } from 'src/app/models/domain';
import { SpecificationDataService } from 'src/app/services';
import { EquipmentFacadeService } from 'src/app/services/equipment-facade.service';
import { handleError } from 'src/app/state/akita-error-helper';
import { CollectionState } from 'src/app/state/collection-state';
import { DesignService } from 'src/app/state/design.service';
import { ItemUiState } from 'src/app/state/item-ui-state';
import { OperationsService } from 'src/app/state/operations';
import { IPage } from 'src/app/state/paged-collection';
import { upsertById } from 'src/app/state/upsert-by-id';

@Injectable({ providedIn: 'root' })
export class TechnicalSpecificationService {
  private readonly state = new CollectionState<TaskListHeader>();
  private readonly uiState = new ItemUiState<ISpecificationTableUI>();

  public readonly error$ = this.state.error$;
  public readonly uiById$ = this.uiState.byId$;

  /** See the matching note on ControlSpecificationService — EcoService reads this. */
  public readonly taskListHeaders$ = this.state.items$;

  /** The design or equipment the accumulated headers belong to; see `accumulate`. */
  private accumulatedFor?: string;

  public constructor(
    private readonly specificationDataService: SpecificationDataService,
    private readonly equipmentFacade: EquipmentFacadeService,
    private readonly operationsService: OperationsService,
    private readonly designService: DesignService
  ) {}

  public getPage(pagination: ITablePagination): Observable<IPage<TaskListHeader>> {
    // Starting a load clears the previous error, which CustomEntityStore.setLoading(true)
    // used to do. Without it a transient failure sticks: the template renders the error
    // instead of the table, so a later success would never be seen.
    this.state.setError(undefined);

    return this.designService.design$.pipe(
      filterUndefined(() => {
        this.state.reset();
      }),
      switchMap((design) => {
        if (design.materialGroup === '0121') {
          return catchAndContinue(
            this.specificationDataService.getTechnicalSpecificationPageByDesignId(
              design.designId,
              pagination
            ),
            (error) => {
              handleError(this.state, error);
            }
          ).pipe(map((result) => ({ context: design.designId, result })));
        }

        return this.equipmentFacade.selectedEquipmentId$.pipe(
          filterUndefined(() => {
            this.state.reset();
          }),
          switchMap((equipmentNumber) =>
            catchAndContinue(
              this.specificationDataService.getTechnicalSpecificationPageByEquipmentNumber(
                equipmentNumber,
                pagination
              ),
              (error) => {
                handleError(this.state, error);
              }
            ).pipe(map((result) => ({ context: equipmentNumber, result })))
          )
        );
      }),
      tap(({ context, result: { data } }) => {
        this.accumulate(context, data);
      }),
      map(({ result: { data, total } }): IPage<TaskListHeader> => ({
        currentPage: pagination.page,
        data,
        lastPage: Math.ceil(total / pagination.perPage),
        perPage: pagination.perPage,
        total,
      }))
    );
  }

  public getAll(): Observable<IPage<TaskListHeader>> {
    this.state.setError(undefined);

    return this.equipmentFacade.selectedEquipmentId$.pipe(
      filterUndefined(() => {
        this.state.reset();
      }),
      switchMap((equipmentNumber) =>
        catchAndContinue(
          this.specificationDataService.getAllTechnicalSpecificationsByEquipmentNumber(
            equipmentNumber
          ),
          (error) => {
            handleError(this.state, error);
          }
        ).pipe(map((result) => ({ context: equipmentNumber, result })))
      ),
      tap(({ context, result: { data } }) => {
        this.accumulate(context, data);
      }),
      // One big page: the "show all" toggle renders everything without a pager.
      map(({ result: { data, total } }): IPage<TaskListHeader> => ({
        currentPage: 1,
        data,
        lastPage: 1,
        perPage: total,
        total,
      }))
    );
  }

  public populateOperationsByTaskListHeader(taskListHeader: TaskListHeader) {
    this.updateUiState(taskListHeader.id, { loading: true, isExpanded: true });

    return this.operationsService.loadByTaskListHeader(taskListHeader).pipe(
      tap(() => {
        this.updateUiState(taskListHeader.id, { loading: false, isExpanded: true });
      })
    );
  }

  public updateUiState(taskListHeaderId: string, ui: ISpecificationTableUI) {
    this.uiState.set(taskListHeaderId, ui);
  }

  /** See the matching note on ControlSpecificationService, including why it is scoped. */
  private accumulate(context: string, headers: TaskListHeader[]): void {
    const items = context === this.accumulatedFor ? this.state.items : [];

    this.accumulatedFor = context;
    this.state.set(upsertById(items, headers));
  }
}
